import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import SEO from "@/components/SEO";
import { ShieldWm } from "@/components/Watermark";
import { FadeUp, MaskedLine, Eyebrow } from "@/components/Reveal";
import { QuoteDateField, QuoteTimeField } from "@/components/QuoteDateTime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPOLOGIE = [
  ["Private transfer", "Trasferimento privato"],
  ["Airport", "Aeroporto"],
  ["Business", "Business"],
  ["Event / Ceremony", "Evento / Cerimonia"],
  ["Long distance", "Lunga distanza"],
  ["Bespoke service", "Servizio personalizzato"],
  ["Other", "Altro"],
];

const INITIAL = {
  nome: "", email: "", telefono: "", azienda: "",
  data: "", ora: "", partenza: "", destinazione: "",
  passeggeri: "1", bagagli: "", tipologia: "", viaggio: "Sola andata",
  note: "", privacy: false, website: "",
};

function validate(f) {
  const e = {};
  if (!f.nome || f.nome.trim().length < 2) e.nome = "Please enter your full name.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = "Please enter a valid email address.";
  if (!f.telefono || f.telefono.replace(/\D/g, "").length < 6) e.telefono = "Please enter a valid phone number.";
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  if (!f.data) e.data = "Please enter the travel date.";
  else if (f.data < todayStr) e.data = "Please enter a valid date.";
  if (!f.ora) e.ora = "Please enter the departure time.";
  if (!f.partenza || f.partenza.trim().length < 2) e.partenza = "Please enter the departure location.";
  if (!f.destinazione || f.destinazione.trim().length < 2) e.destinazione = "Please enter the destination.";
  const p = parseInt(f.passeggeri, 10);
  if (!p || p < 1 || p > 50) e.passeggeri = "Please indicate the number of passengers (1-50).";
  if (f.bagagli && (parseInt(f.bagagli, 10) < 0 || parseInt(f.bagagli, 10) > 50)) e.bagagli = "Invalid number of pieces of luggage.";
  if (!f.tipologia) e.tipologia = "Please select the type of service.";
  if (!f.privacy) e.privacy = "You must accept the Privacy Policy.";
  return e;
}

const SectionLabel = ({ children }) => (
  <div className="mt-16 flex items-center gap-4 first:mt-0">
    <span className="h-px w-8 bg-gold/60" />
    <span className="label-cap">{children}</span>
  </div>
);

const Field = ({ label, required, error, children }) => (
  <div className={`${error ? "field-error" : ""}`}>
    <label className="label-cap mb-2 block !text-[9px]">
      {label} {required && <span className="text-gold">*</span>}
    </label>
    {children}
    {error && <p className="error-msg" data-testid="field-error">{error}</p>}
  </div>
);

export default function QuoteEn() {
  const [f, setF] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [globalError, setGlobalError] = useState("");

  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));

  const submit = async (ev) => {
    ev.preventDefault();
    setGlobalError("");
    const errs = validate(f);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSending(true);
    try {
      await axios.post(`${API}/preventivo`, {
        nome: f.nome.trim(),
        email: f.email.trim(),
        telefono: f.telefono.trim(),
        azienda: f.azienda.trim() || null,
        data: f.data,
        ora: f.ora,
        partenza: f.partenza.trim(),
        destinazione: f.destinazione.trim(),
        passeggeri: parseInt(f.passeggeri, 10),
        bagagli: f.bagagli ? parseInt(f.bagagli, 10) : null,
        tipologia: f.tipologia,
        viaggio: f.viaggio,
        note: f.note.trim() || null,
        privacy: f.privacy,
        website: f.website,
      });
      setDone(true);
      if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
      else window.scrollTo(0, 0);
    } catch (err) {
      setGlobalError(
        err.response && err.response.status === 429
          ? "You have sent too many requests. Please try again later or contact us directly."
          : "There was an error sending your request. Please try again in a few moments."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <SEO
        title="Request a Quote | Zaichauffer – Private Chauffeur"
        description="Request a personalised quote for your transfer with a private chauffeur: airports, cities, business and events throughout Italy."
      />
      <section className="relative isolate mx-auto max-w-7xl overflow-hidden px-6 pb-24 pt-40 md:pb-36 md:pt-52" data-testid="preventivo-page">
      <ShieldWm className="-z-10 -right-24 top-10 w-[400px] opacity-[0.04]" />
        <FadeUp><Eyebrow>Request a quote</Eyebrow></FadeUp>
        <h1 className="mt-10 font-serif text-4xl leading-[1.08] tracking-tight text-bone sm:text-5xl lg:text-6xl">
          <MaskedLine>REQUEST YOUR QUOTE</MaskedLine>
        </h1>
        <FadeUp delay={0.2}>
          <p className="mt-8 max-w-2xl text-[15px] font-light leading-relaxed text-mute">
            Fill in the form. Khalid will receive your request directly and will contact you to
            arrange the service.
          </p>
        </FadeUp>

        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            data-testid="quote-success-message"
            className="mt-16 max-w-3xl border border-gold/30 bg-coal p-12 text-center md:p-20"
          >
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-gold/60">
              <Check size={22} className="text-gold" />
            </span>
            <h2 className="mt-8 font-serif text-2xl leading-snug text-bone md:text-3xl">
              Request sent successfully.
            </h2>
            <p className="mt-4 text-[15px] font-light leading-relaxed text-mute">
              Thank you for contacting Zaichauffer. Khalid will get back to you shortly.
            </p>
          </motion.div>
        ) : (
          <form data-testid="quote-form" onSubmit={submit} noValidate className="mt-16 max-w-3xl">
            <SectionLabel>Client details</SectionLabel>
            <div className="mt-8 grid gap-x-10 gap-y-10 sm:grid-cols-2">
              <Field label="Full name" required error={errors.nome}>
                <input data-testid="quote-form-name-input" className="field" type="text" placeholder="John Smith" value={f.nome} onChange={set("nome")} />
              </Field>
              <Field label="Email" required error={errors.email}>
                <input data-testid="quote-form-email-input" className="field" type="email" placeholder="john@example.com" value={f.email} onChange={set("email")} />
              </Field>
              <Field label="Phone number" required error={errors.telefono}>
                <input data-testid="quote-form-phone-input" className="field" type="tel" placeholder="+39 333 000 0000" value={f.telefono} onChange={set("telefono")} />
              </Field>
              <Field label="Company (optional)" error={errors.azienda}>
                <input data-testid="quote-form-company-input" className="field" type="text" placeholder="Company name" value={f.azienda} onChange={set("azienda")} />
              </Field>
            </div>

            <SectionLabel>Trip details</SectionLabel>
            <div className="mt-8 grid gap-x-10 gap-y-10 sm:grid-cols-2">
              <Field label="Travel date" required error={errors.data}>
                <QuoteDateField lang="en" value={f.data} onChange={(v) => setF((s) => ({ ...s, data: v }))} />
              </Field>
              <Field label="Departure time" required error={errors.ora}>
                <QuoteTimeField lang="en" value={f.ora} onChange={(v) => setF((s) => ({ ...s, ora: v }))} />
              </Field>
              <Field label="Departure location" required error={errors.partenza}>
                <input data-testid="quote-form-pickup-input" className="field" type="text" placeholder="Address, city or airport" value={f.partenza} onChange={set("partenza")} />
              </Field>
              <Field label="Destination" required error={errors.destinazione}>
                <input data-testid="quote-form-destination-input" className="field" type="text" placeholder="Address, city or airport" value={f.destinazione} onChange={set("destinazione")} />
              </Field>
              <Field label="Number of passengers" required error={errors.passeggeri}>
                <input data-testid="quote-form-passengers-input" className="field" type="number" min="1" max="50" value={f.passeggeri} onChange={set("passeggeri")} />
              </Field>
              <Field label="Number of pieces of luggage" error={errors.bagagli}>
                <input data-testid="quote-form-luggage-input" className="field" type="number" min="0" max="50" placeholder="If known" value={f.bagagli} onChange={set("bagagli")} />
              </Field>
            </div>

            <div className="mt-10">
              <label className="label-cap mb-3 block !text-[9px]">Trip type</label>
              <div className="flex flex-wrap gap-3">
                {["Sola andata", "Andata e ritorno"].map((v) => (
                  <button
                    key={v}
                    type="button"
                    data-testid={`quote-form-trip-${v === "Sola andata" ? "andata" : "ritorno"}`}
                    onClick={() => setF((s) => ({ ...s, viaggio: v }))}
                    className={`border px-5 py-3 text-[10px] tracking-[0.18em] uppercase transition-colors duration-300 ${
                      f.viaggio === v ? "border-gold text-gold" : "border-white/15 text-bone/60 hover:border-gold/50"
                    }`}
                  >
                    {v === "Sola andata" ? "One way" : "Round trip"}
                  </button>
                ))}
              </div>
            </div>

            <SectionLabel>Type of service</SectionLabel>
            <div className={`mt-8 ${errors.tipologia ? "field-error" : ""}`}>
              <Field label="Select the service" required error={errors.tipologia}>
                <select data-testid="quote-form-service-select" className="field" value={f.tipologia} onChange={set("tipologia")}>
                  <option value="">Select a service…</option>
                  {TIPOLOGIE.map(([en, it]) => (
                    <option key={it} value={it}>{en}</option>
                  ))}
                </select>
              </Field>
            </div>

            <SectionLabel>Notes</SectionLabel>
            <div className="mt-8">
              <Field label="Special requirements, intermediate stops, flights or any other useful information" error={errors.note}>
                <textarea
                  data-testid="quote-form-notes-input"
                  className="field"
                  placeholder="Tell us about any special requirements, intermediate stops, times, flights or other useful information."
                  value={f.note}
                  onChange={set("note")}
                />
              </Field>
            </div>

            <div className="hidden" aria-hidden="true">
              <label>Website</label>
              <input type="text" name="website" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} />
            </div>

            <div className="mt-14">
              <label className="flex cursor-pointer items-start gap-4">
                <input
                  data-testid="quote-form-privacy-checkbox"
                  type="checkbox"
                  className="lux-check mt-0.5"
                  checked={f.privacy}
                  onChange={(e) => setF((s) => ({ ...s, privacy: e.target.checked }))}
                />
                <span className="text-[13px] font-light leading-relaxed text-bone/75">
                  I have read and accept the{" "}
                  <a href="/en/privacy-policy" className="text-gold underline-offset-4 hover:underline">
                    Privacy Policy
                  </a>{" "}
                  and I consent to the processing of my personal data for the purpose of handling this
                  request. *
                </span>
              </label>
              {errors.privacy && <p className="error-msg" data-testid="privacy-error">{errors.privacy}</p>}
            </div>

            {globalError && (
              <div data-testid="quote-error-message" role="alert" className="mt-8 border border-red-900/50 bg-red-950/30 px-6 py-4 text-sm text-red-200">
                {globalError}
              </div>
            )}

            <button data-testid="quote-form-submit-button" type="submit" disabled={sending} className="btn-solid mt-12 disabled:cursor-wait disabled:opacity-60">
              {sending ? "Sending…" : "Send request"}
            </button>
            <p className="mt-6 text-xs font-light text-mute/70">
              Fields marked with <span className="text-gold">*</span> are required.
            </p>
          </form>
        )}
      </section>
    </>
  );
}
